import { useEffect, useMemo, useState } from "react";
import { Link, Route, Routes, useNavigate } from "react-router-dom";
import Carregando from "../componentes/Carregando";
import { Cabine } from "../componentes/FerramentaUi";
import { TileDeFato } from "../componentes/CalendarioUi";
import { lerPainel, tempoLegivel, type MetaNaLista, type Painel } from "../lib/gerenciadorEstudos";
import MetaDoCronograma from "./GerenciadorMeta";

// O Gerenciador de Estudos — o cronograma de 275 metas do Aprovação Contábil
// (000117), portado do Bubble em 27/08/2026.
//
// ---------------------------------------------------------------------------
// Duas telas, e o segundo segmento é o NÚMERO da meta
// ---------------------------------------------------------------------------
// `/ferramenta/gerenciador-de-estudos`            o índice + o progresso
// `/ferramenta/gerenciador-de-estudos/meta/12`    a meta 12
//
// O número é a identidade da meta (denso 1..275, sem buraco), então ele serve
// de rota sem tradução nenhuma. É o mesmo desenho do HUB DP: sub-rota, nunca
// `useState`, para o aluno voltar pelo navegador e poder mandar o link de uma
// meta para o colega.
//
// ---------------------------------------------------------------------------
// Por que "12 de 275" e não "4,4%"
// ---------------------------------------------------------------------------
// A tela do Bubble mostra uma barra com a porcentagem, e ela tem um defeito
// silencioso: o denominador SE MOVE. O cronograma cresceu cinco vezes, a última
// em fevereiro de 2026 com 67 metas de uma vez — quem concluiu tudo em fev/2025
// via 100% e hoje veria 75,6%. **A barra anda para trás sem o aluno fazer
// nada**, e o princípio 1 desta casa diz que errar para menos é o que não tem
// volta.
//
// Aqui o número herói é a CONTAGEM, que nunca regride, e a porcentagem entra
// como apoio, com o total ao lado. Quando o catálogo crescer, "12 de 275" vira
// "12 de 342": o aluno vê que o plano cresceu, em vez de ver o próprio esforço
// encolher.
//
// ---------------------------------------------------------------------------
// O índice é LISTA, não grade de cards
// ---------------------------------------------------------------------------
// São 275 metas e o uso real é procurar uma pelo número. Grade de cards gastaria
// um bloco de 120px para mostrar "Meta 102" — é a cicatriz do IR Pro, que saiu
// de 8.249px para 1.120px com o mesmo conteúdo quando o índice virou texto.

export default function GerenciadorDeEstudos() {
  return (
    <Routes>
      <Route index element={<Entrada />} />
      <Route path="meta/:numero" element={<MetaDoCronograma />} />
      <Route path="*" element={<Entrada />} />
    </Routes>
  );
}

/** Quantas metas mostrar antes do "mostrar mais". */
const LOTE = 40;

function Entrada() {
  const [painel, setPainel] = useState<Painel | null>(null);
  const [erro, setErro] = useState(false);
  const [filtro, setFiltro] = useState<"todas" | "feitas" | "abertas">("todas");
  const [busca, setBusca] = useState("");
  const [mostrando, setMostrando] = useState(LOTE);
  const navegar = useNavigate();

  useEffect(() => {
    let vivo = true;
    lerPainel().then((p) => {
      if (!vivo) return;
      if (!p) setErro(true);
      else setPainel(p);
    });
    return () => {
      vivo = false;
    };
  }, []);

  const visiveis = useMemo(() => {
    if (!painel) return [];
    const n = Number(busca.trim());
    return painel.metas.filter((m) => {
      if (filtro === "feitas" && !m.feita) return false;
      if (filtro === "abertas" && m.feita) return false;
      if (busca.trim() && Number.isFinite(n) && m.numero !== n) return false;
      return true;
    });
  }, [painel, filtro, busca]);

  if (erro) {
    return (
      <div className="mx-auto w-full max-w-5xl px-4 py-8 sm:px-6">
        <p className="surface p-6 text-sm text-tinta">
          Não conseguimos carregar o seu cronograma agora. Recarregue a página em alguns
          instantes.
        </p>
      </div>
    );
  }
  if (!painel) return <Carregando />;

  const { progresso: p } = painel;
  const pct = p.metas_total ? Math.round((p.metas_feitas / p.metas_total) * 1000) / 10 : 0;

  return (
    <div className="mx-auto w-full max-w-5xl px-4 py-8 sm:px-6">
      <Cabine
        rotulo="FERRAMENTA"
        titulo="Gerenciador de Estudos"
        descricao={
          <>
            O cronograma do Aprovação Contábil, meta a meta: as aulas de cada etapa, o que
            aprender, a dica do professor e os cadernos de questões. Marque o que concluir,
            anote o que quiser lembrar e use o cronômetro para acompanhar o seu tempo de
            estudo.
          </>
        }
        fatos={
          <>
            <TileDeFato
              rotulo="Metas concluídas"
              valor={
                <>
                  {p.metas_feitas}
                  <span className="text-navy-mute"> de {p.metas_total}</span>
                </>
              }
              apoio={p.metas_feitas > 0 ? `${pct}% do cronograma` : "comece pela meta 1"}
            />
            {/* Tile de fato só existe com dado medido: quem nunca ligou o
                cronômetro não recebe um "0h 0min" grande, que é o que a tela do
                Bubble faz e o olho lê como defeito. O tempo soma o cronometrado no
                Bubble e no hub, com o teto de 24 h por dia (000363). */}
            {p.dias_estudados > 0 && (
              <TileDeFato
                rotulo="Tempo cronometrado"
                valor={tempoLegivel(p.estudo_total_ms)}
                apoio={`em ${p.dias_estudados} ${p.dias_estudados === 1 ? "dia" : "dias"}`}
              />
            )}
            {p.proxima_meta !== null && (
              <TileDeFato
                rotulo="Retomar em"
                valor={`Meta ${p.proxima_meta}`}
                apoio="a primeira que ainda falta"
              />
            )}
          </>
        }
        acao={
          p.proxima_meta !== null ? (
            <Link to={`meta/${p.proxima_meta}`} className="pill-cta">
              Retomar meu plano de estudos
            </Link>
          ) : (
            <span className="text-sm text-navy-mute">
              Você marcou todas as metas do cronograma.
            </span>
          )
        }
      />

      <section
        role="group"
        aria-labelledby="titulo-cronograma"
        className="mt-8"
      >
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <h2 id="titulo-cronograma" className="font-display text-xl font-bold text-titulo">
              O cronograma
            </h2>
            <p className="pt-1 text-sm text-cinza">
              {p.metas_total} metas em sequência. Cada uma reúne as aulas daquela etapa.
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <div className="flex rounded-full bg-chip p-1">
              {(["todas", "abertas", "feitas"] as const).map((f) => (
                <button
                  key={f}
                  type="button"
                  onClick={() => {
                    setFiltro(f);
                    setMostrando(LOTE);
                  }}
                  aria-pressed={filtro === f}
                  className={`rounded-full px-3 py-1.5 text-sm font-semibold transition ${
                    filtro === f ? "bg-superficie text-titulo shadow-sm" : "text-cinza hover:text-tinta"
                  }`}
                >
                  {f === "todas" ? "Todas" : f === "abertas" ? "A fazer" : "Concluídas"}
                </button>
              ))}
            </div>

            <label className="flex items-center gap-2 text-sm">
              <span className="sr-only">Ir para a meta de número</span>
              <input
                type="search"
                inputMode="numeric"
                value={busca}
                onChange={(e) => setBusca(e.target.value)}
                onKeyDown={(e) => {
                  const n = Number(busca.trim());
                  if (e.key === "Enter" && Number.isFinite(n) && n > 0) navegar(`meta/${n}`);
                }}
                placeholder="Ir para a meta…"
                className="w-36 rounded-full border border-borda bg-superficie px-4 py-1.5 text-sm text-tinta placeholder:text-cinza focus-visible:outline-none focus-visible:ring-3 focus-visible:ring-laranja/40"
              />
            </label>
          </div>
        </div>

        {visiveis.length === 0 ? (
          <p className="surface mt-4 p-6 text-sm text-cinza">
            {filtro === "feitas"
              ? "Você ainda não marcou nenhuma meta como concluída."
              : busca.trim()
                ? `Não há meta com o número ${busca.trim()}.`
                : "Nenhuma meta para mostrar."}
          </p>
        ) : (
          <>
            <ul className="mt-4 grid gap-2 sm:grid-cols-2">
              {visiveis.slice(0, mostrando).map((m) => (
                <LinhaDaMeta key={m.id} meta={m} />
              ))}
            </ul>
            {visiveis.length > mostrando && (
              <div className="mt-4 text-center">
                <button
                  type="button"
                  onClick={() => setMostrando((n) => n + LOTE)}
                  className="btn-contorno"
                >
                  Mostrar mais {Math.min(LOTE, visiveis.length - mostrando)} de{" "}
                  {visiveis.length - mostrando}
                </button>
              </div>
            )}
          </>
        )}
      </section>
    </div>
  );
}

/**
 * Uma linha do índice: número, o que a meta tem, e se está concluída.
 *
 * A meta não tem título no Bubble — o `nome` é literalmente "META 102", sempre
 * derivável do número (e com a caixa oscilando entre "META" e "Meta" conforme o
 * lote de cadastro). Então o que identifica a meta na lista é o NÚMERO, e o que
 * a descreve é o conteúdo dela: quantas aulas, se tem caderno, se tem texto.
 *
 * Meta vazia é dita com letra. São 4 (272 a 275, cadastradas em fev/2026 e nunca
 * preenchidas) e mais 23 cujas aulas não têm link que funcione — 27 no total,
 * 9,8% do cronograma, com sete seguidas da 202 à 208. Ausência de linha não
 * informa nada: sem esse aviso, o aluno abre e acha que a tela quebrou.
 */
function LinhaDaMeta({ meta }: { meta: MetaNaLista }) {
  const vazia = meta.aulas === 0;
  return (
    <li>
      <Link
        to={`meta/${meta.numero}`}
        className="surface porta flex items-center gap-4 px-4 py-3"
      >
        <span
          aria-hidden="true"
          className={`grid size-10 shrink-0 place-items-center rounded-[13px] font-display text-sm font-bold tabular-nums ${
            meta.feita ? "bg-laranja text-navy-ink" : "bg-tile text-laranja-texto"
          }`}
        >
          {meta.numero}
        </span>
        <span className="min-w-0 flex-1">
          <span className="block font-semibold text-titulo">
            Meta {meta.numero}
            {meta.feita && (
              <span className="ml-2 rounded-full bg-chip px-2 py-0.5 text-[0.6875rem] font-semibold uppercase tracking-wide text-cinza">
                concluída
              </span>
            )}
          </span>
          <span className="block pt-0.5 text-sm text-cinza">
            {vazia ? (
              "Ainda sem aula publicada"
            ) : (
              <>
                {meta.aulas} {meta.aulas === 1 ? "aula" : "aulas"}
                {meta.cadernos > 0 && ` · ${meta.cadernos} caderno${meta.cadernos === 1 ? "" : "s"}`}
                {/* Só a FALTA vira etiqueta. "Com orientação" apareceria em 179
                    das 275 metas e não separaria nada — a captura de 27/08
                    mostrou a mesma linha repetida 15 vezes numa tela. */}
                {!meta.temLink && " · aulas ainda sem link"}
              </>
            )}
          </span>
        </span>
      </Link>
    </li>
  );
}
